import { useState, useEffect } from "react";
import { api, apiError } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Copy, Users, Target, Mail } from "lucide-react";

export default function AgentDashboard() {
  const [data, setData] = useState(null);

  const load = async () => { try { const { data } = await api.get("/agent/summary"); setData(data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);

  const copyCode = () => { navigator.clipboard.writeText(data?.agent_code || ""); toast.success("Referral code copied!"); };

  return (
    <DashboardShell title="Agent dashboard" subtitle="Track your registered members and progress">
      <div className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white rounded-2xl p-6 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="agent-code-banner">
        <div>
          <p className="text-xs text-emerald-100 uppercase tracking-wide">Your referral code</p>
          <p className="text-3xl font-mono-gdf font-bold mt-1" data-testid="agent-code">{data?.agent_code || "—"}</p>
          <p className="text-xs text-emerald-100 mt-1">Share this so members can link to you at registration.</p>
        </div>
        <Button onClick={copyCode} data-testid="copy-code-btn" className="bg-white/15 hover:bg-white/25 text-white"><Copy className="w-4 h-4 mr-1" /> Copy code</Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="agent-stat-total">
          <div className="flex items-center gap-2 text-emerald-600"><Users className="w-5 h-5" /><p className="text-xs uppercase tracking-wide text-slate-500">Members registered</p></div>
          <p className="text-3xl font-head font-bold text-[#0A192F] mt-2">{data?.total_customers ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="agent-stat-active">
          <div className="flex items-center gap-2 text-emerald-600"><Target className="w-5 h-5" /><p className="text-xs uppercase tracking-wide text-slate-500">Active contributors</p></div>
          <p className="text-3xl font-head font-bold text-[#0A192F] mt-2">{data?.active_customers ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="agent-stat-goal">
          <p className="text-xs uppercase tracking-wide text-slate-500">Goal progress</p>
          <p className="text-lg font-head font-bold text-[#0A192F] mt-2">{data?.total_customers ?? 0} of {data?.goal ?? 50}</p>
          <Progress value={data?.progress_pct || 0} className="mt-2 h-2" />
        </div>
      </div>

      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" data-testid="agent-tab-customers">My members</TabsTrigger>
          <TabsTrigger value="messages" data-testid="agent-tab-messages"><Mail className="w-4 h-4 mr-1" /> Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="customers">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4">
            {data?.customers?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="agent-customers-table">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Name</th><th>Reference</th><th>Package</th><th>Contributed</th><th>Months</th></tr></thead>
                  <tbody>
                    {data.customers.map((c) => (
                      <tr key={c.user_id} className="border-b border-slate-100">
                        <td className="py-2 font-medium">{c.name}</td>
                        <td className="font-mono-gdf">{c.customer_reference}</td>
                        <td className="capitalize">{c.package || "—"}</td>
                        <td>R{c.total_contributed?.toLocaleString() || 0}</td>
                        <td>{c.months_paid || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-slate-400 text-sm">No members registered under your code yet. Share your code to get started!</p>}
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 space-y-3">
            {data?.messages?.length ? data.messages.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-xl p-4" data-testid="agent-message">
                <div className="flex justify-between items-start"><p className="font-semibold text-[#0A192F]">{m.subject}</p><Badge className="bg-slate-100 text-slate-600">{new Date(m.created_at).toLocaleDateString()}</Badge></div>
                <p className="text-sm text-slate-600 mt-1">{m.body}</p>
                <p className="text-xs text-emerald-600 mt-2">From {m.from_name} · <a href={`mailto:${m.from_email}`} className="underline">{m.from_email}</a></p>
              </div>
            )) : <p className="text-slate-400 text-sm">No messages yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
